import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';

const LawyerVerification = ({ compact = false }) => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('lawyer_verifications')
        ?.select(`
          *,
          lawyer:lawyer_id (
            full_name,
            email,
            phone
          )
        `)
        ?.order('submitted_at', { ascending: false });

      if (error) throw error;
      setVerifications(data || []);
    } catch (error) {
      console.error('Error fetching verifications:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationAction = async (verificationId, action, notes = '') => {
    try {
      const newStatus = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'under_review';
      const currentUser = await supabase?.auth?.getUser();

      const { error } = await supabase
        ?.from('lawyer_verifications')
        ?.update({
          verification_status: newStatus,
          verification_notes: notes,
          verified_by: currentUser?.data?.user?.id,
          verified_at: new Date()?.toISOString()
        })
        ?.eq('id', verificationId);

      if (error) throw error;

      // Update user status if approved
      if (action === 'approve') {
        const verification = verifications?.find(v => v?.id === verificationId);
        await supabase
          ?.from('user_profiles')
          ?.update({ status: 'active' })
          ?.eq('id', verification?.lawyer_id);
      }

      // Log action
      await supabase?.rpc('log_user_action', {
        p_user_id: currentUser?.data?.user?.id,
        p_action: `lawyer_verification_${action}`,
        p_resource_type: 'verification',
        p_resource_id: verificationId,
        p_severity: 'warning'
      });

      fetchVerifications();
      setSelectedVerification(null);
    } catch (error) {
      console.error('Error updating verification:', error?.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Pending', icon: 'Clock' },
      under_review: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Under Review', icon: 'Eye' },
      approved: { bg: 'bg-success-100', text: 'text-success-700', label: 'Approved', icon: 'CheckCircle' },
      rejected: { bg: 'bg-error-100', text: 'text-error-700', label: 'Rejected', icon: 'XCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const pendingVerifications = verifications?.filter(v => v?.verification_status === 'pending');
  const displayVerifications = compact ? pendingVerifications?.slice(0, 3) : verifications;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-secondary-100 rounded w-1/3"></div>
          <div className="h-32 bg-secondary-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-bold text-text-primary">Lawyer Verification</h2>
            <p className="text-sm text-text-secondary mt-1">
              {pendingVerifications?.length} pending KYC approvals
            </p>
          </div>
          {compact && pendingVerifications?.length > 3 && (
            <button className="text-primary text-sm font-medium hover:underline">
              View All
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {displayVerifications?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="ShieldCheck" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">No verification requests</p>
          </div>
        ) : (
          displayVerifications?.map((verification) => (
            <div key={verification?.id} className="p-6 hover:bg-secondary-50 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-text-primary">{verification?.lawyer?.full_name}</h3>
                      <p className="text-sm text-text-secondary">{verification?.lawyer?.email}</p>
                    </div>
                  </div>
                  <div className="ml-13 space-y-2">
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="FileText" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">Bar Council: </span>
                      <span className="text-text-primary font-medium">{verification?.bar_council_number}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="Calendar" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">Submitted: </span>
                      <span className="text-text-primary">{format(new Date(verification?.submitted_at), 'MMM dd, yyyy')}</span>
                    </div>
                    {verification?.verification_notes && (
                      <div className="flex items-start space-x-2 text-sm">
                        <Icon name="MessageSquare" size={14} className="text-text-secondary mt-0.5" />
                        <span className="text-text-secondary">{verification?.verification_notes}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  {getStatusBadge(verification?.verification_status)}
                  {verification?.verification_status === 'pending' && (
                    <div className="flex items-center space-x-2 mt-2">
                      <button
                        onClick={() => handleVerificationAction(verification?.id, 'approve', 'KYC documents verified and approved')}
                        className="flex items-center space-x-1 px-3 py-1 bg-success text-white rounded-lg text-sm hover:bg-success-700 transition-smooth"
                      >
                        <Icon name="Check" size={14} />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleVerificationAction(verification?.id, 'reject', 'Documents incomplete or invalid')}
                        className="flex items-center space-x-1 px-3 py-1 bg-error text-white rounded-lg text-sm hover:bg-error-700 transition-smooth"
                      >
                        <Icon name="X" size={14} />
                        <span>Reject</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LawyerVerification;